import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import { DealData } from "@/pages/deals/types";
import { LeadData } from "@/pages/leads/types";
import { Add, OpenTasks as OpenTasksIcon } from "@dreamhub/designsystem/icons";
import { Button } from "@/components/ui/button";
import CounterBadge from "@/components/ui/counter-badge";
import {
  useFilteredTasks
} from "@/entities/tasks/hooks";
import Task from "@/components/controls/Task/Task";
import { TaskModalState } from "@/entities/tasks/types";
import Spinner from "@/components/ui/spinner";
import { IconButton } from "@/components/ui/icon-button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { map } from "lodash";
import { PersonData } from "@/entities/people/types";
import { CompanyData } from "@/pages/companies/types";
import { PartnerData } from "@/pages/partners/types";
import { ContractData } from "@/pages/contracts/types";

const TRANSLATION_KEY = "deals.nextSteps.openTasks";
const INITIAL_TASKS_COUNT = 2;
const TASKS_PAGE_COUNT = 5;

interface Props {
  entity:
    | DealData
    | LeadData
    | PersonData
    | CompanyData
    | PartnerData
    | ContractData
    | undefined;
  setTaskModalState: React.Dispatch<React.SetStateAction<TaskModalState>>;
  isQuickView?: boolean;
  toggleTasksDrawer?: () => void;
}

const OpenTasks: React.FC<Props> = ({
  entity,
  setTaskModalState,
  isQuickView = false,
  toggleTasksDrawer
}) => {
  const { t } = useTranslation();
  const [visibleCount, setVisibleCount] = useState(INITIAL_TASKS_COUNT);

  useEffect(() => {
    setVisibleCount(INITIAL_TASKS_COUNT);
  }, [entity]);

  const { entities: tasks, isLoading: isLoadingTasks } = useFilteredTasks(
    {
      entityIds: { contains: entity?.id || "" },
      isCompleted: false
    },
    undefined,
    undefined,
    undefined,
    "dueDate",
    true
  );


  const openTasks = tasks.slice(
    0,
    isQuickView ? visibleCount : INITIAL_TASKS_COUNT
  );
  const shouldShowMore = isQuickView
    ? tasks.length > visibleCount
    : tasks.length > INITIAL_TASKS_COUNT;

  const handleShowMore = () => {
    setVisibleCount((prev: number) =>
      Math.min(prev + TASKS_PAGE_COUNT, tasks.length)
    );
  };

  const showMoreTasks = () => {
    if (isQuickView) {
      handleShowMore();
    } else {
      toggleTasksDrawer?.();
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex w-full justify-between items-center">
        <div className="flex gap-2 items-center">
          <Text variant="bodySecondary" className="text-h2">
            {t(`${TRANSLATION_KEY}.title`)}
          </Text>
          {!isLoadingTasks && tasks?.length > 0 && (
            <CounterBadge count={tasks?.length} size="sm" />
          )}
        </div>
        <Tooltip>
          <TooltipTrigger asChild>
            <IconButton
              onClick={() =>
                setTaskModalState({
                  editTaskId: null,
                  isEditMode: false,
                  isOpen: true
                })
              }
              size="md"
              variant="iconNoBg"
              iconColor="secondary"
            >
              <Add />
            </IconButton>
          </TooltipTrigger>
          <TooltipContent>{t(`${TRANSLATION_KEY}.add`)}</TooltipContent>
        </Tooltip>
      </div>
      {isLoadingTasks ? (
        <div className="w-full h-10 flex justify-center items-center">
          <Spinner size="lg" />
        </div>
      ) : tasks?.length === 0 ? (
        <div className="flex px-2 py-2 w-full flex-col gap-2 items-center">
          {!isQuickView && <OpenTasksIcon />}
          <Text variant="bodyTertiary">{t(`${TRANSLATION_KEY}.empty`)}</Text>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {map(openTasks, (task) => (
            <div key={task.id} className="flex flex-col gap-[2px]">
              <Task
                key={task.id}
                task={task}
                setTaskModalState={setTaskModalState}
                isCompletedTasksView={false}
              />
            </div>
          ))}
          {shouldShowMore && (
            <Button
              variant="tertiary"
              onClick={showMoreTasks}
              className="size-fit self-end"
            >
              {isQuickView
                ? t("showNumMore", {
                    num:
                      tasks.length - visibleCount > TASKS_PAGE_COUNT
                        ? TASKS_PAGE_COUNT
                        : tasks.length - visibleCount
                  })
                : t(`${TRANSLATION_KEY}.showMore`)}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default OpenTasks;
